// src/artist/TitApplicationManage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import titService from '../../services/titService';
import { useToast } from '../../hooks/useToast';
import Button from '../../components/common/Button';
import { useInfiniteQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faEye, faEdit, faRupeeSign, faExclamationTriangle} from '@fortawesome/free-solid-svg-icons';
import { faFacebook, faTwitter, faInstagram, faDebian } from '@fortawesome/free-brands-svg-icons';
import '../../styles/TokenApplicationManagement.css';



const ApplicationCard = ({ application, onViewDetails }) => {
  const [showDetails, setShowDetails] = useState(false);
  
  const statusColors = {
    pending: 'var(--warning-color)',
    approved: 'var(--success-color)',
    rejected: 'var(--danger-color)'
  };

  return (
    <div className="application-card">
      <div className="card-header">
        <div className="artist-info">
          <h3>{application.artist_name}</h3>
          <p>{application.artist_email}</p>
        </div>
        <div className="status-badge" style={{ backgroundColor: statusColors[application.application_status] }}>
          {application.application_status}
        </div>
        {application.application_status === 'pending' && (
            <span className="conversion-warning-badge">
            <FontAwesomeIcon icon={faExclamationTriangle} /> TitTat conversion remains.
            </span>
        )}
      </div>
      
      <div className="card-content">
        <div className="info-row">
          <span className="label">Price:</span>
          <span className="value"><FontAwesomeIcon icon={faRupeeSign} /> {application.price}</span>
        </div>
        <div className="info-row">
          <span className="label">Token Units:</span>
          <span className="value">{application.token_unit}</span>
        </div>
        <div className="info-row">
          <span className="label">Time Taken:</span>
          <span className="value">{application.time_taken} hours</span>
        </div>
        <div className="info-row">
          <span className="label"><h4 className="application-id-heading">{application.application_id}</h4></span>
        </div>
        
        {showDetails && (
          <div className="details-section">
            <div className="info-row">
              <span className="label">Definition:</span>
              <span className="value">{application.definition}</span>
            </div>
            <div className="info-row">
              <span className="label">Rarity:</span>
              <span className="value">{application.rarity}/10</span>
            </div>
            <div className="info-row">
              <span className="label">Artist Reputation:</span>
              <span className="value">{application.artist_reputation}/10</span>
            </div>
            
            <div className="social-links">
              {application.instagram_link && (
                <a href={application.instagram_link} target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faInstagram} />
                </a>
              )}
              {application.facebook_link && (
                <a href={application.facebook_link} target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faFacebook} />
                </a>
              )}
              {application.twitter_link && (
                <a href={application.twitter_link} target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faTwitter} />
                </a>
              )}
            </div>
          </div>
        )}
        
        <div className="card-actions">
          <Button 
            variant="secondary" 
            onClick={() => setShowDetails(!showDetails)}
            className="details-btn"
          >
            <FontAwesomeIcon icon={faEye} /> {showDetails ? 'Hide Details' : 'View Details'}
          </Button>
          
          {application.application_status == 'pending' && (
            <Button 
              variant="primary" 
              onClick={() => onViewDetails(application)}
            >
              <FontAwesomeIcon icon={faEdit} /> Edit
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default function TokenApplicationManagement() {
  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const { showToast } = useToast();

  const applicationsQuery = useInfiniteQuery({
    queryKey: ['artist-token-applications', filter],
    initialPageParam: undefined,
    queryFn: ({ pageParam }) => titService.getTokenizationApplicationsClient({
      limit: 20,
      cursor: pageParam,
      status: filter === 'all' ? undefined : filter,
    }),
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.data?.data?.pagination;
      return pagination?.has_more ? pagination?.next_cursor : undefined;
    },
  });

  useEffect(() => {
    const flattened = applicationsQuery.data?.pages?.flatMap((page) => (
      page?.data?.data?.items || page?.data?.data || []
    )) || [];
    setApplications(flattened);
  }, [applicationsQuery.data]);

  useEffect(() => {
    if (applicationsQuery.error) {
      showToast('Failed to load applications', 500);
    }
  }, [applicationsQuery.error, showToast]);


  const handleViewDetails = (application) => {
    setSelectedApplication(application);
    setIsEditModalOpen(true);
  };

  const handleEditApplication = async (updatedData) => {
    try {
      await titService.updateTokenizationApplication(selectedApplication.application_id, updatedData);
      
      setApplications(prev => 
        prev.map(app => 
          app.application_id === selectedApplication.application_id 
            ? { ...app, ...updatedData } 
            : app
        )
      );
      applicationsQuery.refetch();
      
      setIsEditModalOpen(false);
      showToast("Application updated successfully", 200);
    } catch (error) {
      console.error("Failed to update application", error);
      showToast("Failed to update application", 500);
    }
  };

  const filteredApplications = useMemo(() => applications, [applications]);

  return (
    <DashboardLayout>
      <div className="token-applications-container">
        
        <div className="applications-header">
          
          <div className="status-filters">
            <Button 
              variant={filter === 'all' ? 'primary' : 'secondary'} 
              onClick={() => setFilter('all')}
            >
              All
            </Button>
            <Button 
              variant={filter === 'pending' ? 'primary' : 'secondary'} 
              onClick={() => setFilter('pending')}
            >
              Pending
            </Button>
            <Button 
              variant={filter === 'approved' ? 'primary' : 'secondary'} 
              onClick={() => setFilter('approved')}
            >
              Approved
            </Button>
            <Button 
              variant={filter === 'rejected' ? 'primary' : 'secondary'} 
              onClick={() => setFilter('rejected')}
            >
              Rejected
            </Button>

          </div>
        </div>
        
        {applicationsQuery.isLoading ? (
          <div className="loading-container">
            <p>Loading applications...</p>
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="empty-container">
            <p>No token applications found</p>
          </div>
        ) : (
          <div className="applications-list">
            {filteredApplications.map(application => (
              <ApplicationCard 
                key={application.application_id}
                application={application}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>
        )}
        {applicationsQuery.hasNextPage ? (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
            <Button
              variant="secondary"
              onClick={() => applicationsQuery.fetchNextPage()}
              disabled={applicationsQuery.isFetchingNextPage}
            >
              {applicationsQuery.isFetchingNextPage ? 'Loading...' : 'Load More'}
            </Button>
          </div>
        ) : null}
      </div>
    </DashboardLayout>
  );
}